import { Trans } from "@lingui/macro";
import {
  Box,
  AlignItems,
  H3,
  Visibility,
  TextAlign,
  ButtonVariant,
  TypePreset,
  TypeScale,
  Button,
  ButtonSize,
  Space,
} from "@gocardless/flux-react";
import { TemporaryAccessTokenCreateRequestBody } from "@gocardless/api/dashboard/temporary-access-token";

import BankAccountDataBanner from "./BankAccountDataBanner/BankAccountDataBanner";
import { EmailAndPassword } from "./EmailPassword/EmailAndPassword";
import { RedirectMessage } from "./Gc4xRedirectMessage/RedirectMessage";
import { OneTimePassword } from "./otp/OneTimePassword";
import { RecoveryCode } from "./RecoveryCode/RecoveryCode";
import { RequirePasswordResetMessage } from "./RequirePasswordResetMessage/RequirePasswordResetMessage";
import { TooManyFailedAttemptsMessage } from "./TooManyFailedAttemptsMessage/TooManyFailedAttemptsMessage";
import { AuthFactorMetaData } from "./types";
import { SignUpLink } from "./SignIn";
import { SsoNotAvailableMessage } from "./SsoNotAvailableMessage/SsoNotAvailableMessage";
import { OAuthFailureMessage } from "./OauthFailureMessage/OAuthFailureMessage";

import { useOptimizelyVariation } from "src/technical-integrations/optimizely/useOptimizelyVariation";
import { OptimizelyFlag } from "src/technical-integrations/optimizely/constants";

interface Props {
  requireResetPassword: boolean;
  tooManyFailedAttempts: boolean;
  authFactorMetaData: AuthFactorMetaData | undefined;
  hasRecoveryCode: boolean;
  isSubmitting: boolean;
  onSubmitWithPassword: (
    requestBody: TemporaryAccessTokenCreateRequestBody
  ) => void;
  onSubmitWithSso: (email: string) => void;
  onTriggerRecoveryCode: () => void;
  onTriggerOneTimePassword: () => void;
  usingSso: boolean;
  setUsingSso: (usingSso: boolean) => void;
  ssoNotAvailable: boolean;
  setSsoNotAvailable: (usingSso: boolean) => void;
  oauthErrorKey: string | null;
}

export const SignInForm: React.FC<Props> = ({
  requireResetPassword,
  tooManyFailedAttempts,
  authFactorMetaData,
  hasRecoveryCode,
  isSubmitting,
  onSubmitWithPassword,
  onSubmitWithSso,
  onTriggerRecoveryCode: handleTriggerRecoveryCode,
  onTriggerOneTimePassword: handleTriggerOneTimePassword,
  usingSso,
  setUsingSso,
  ssoNotAvailable,
  setSsoNotAvailable,
  oauthErrorKey,
}) => {
  const { isVariationOn: isSsoAvailable, clientReady: isOptimizelyReady } =
    useOptimizelyVariation({
      flag: OptimizelyFlag.SSO_AVAILABLE,
    });

  return (
    <Box
      layout="flex"
      flexDirection="column"
      alignItems={AlignItems.FlexStart}
      width="100%"
    >
      <H3
        preset={TypePreset.Heading_01}
        size={TypeScale.Size_06}
        textAlign={TextAlign.Left}
      >
        <Trans>Sign in to your account</Trans>
      </H3>
      <RedirectMessage />
      {requireResetPassword && (
        <>
          <Space v={1} />
          <RequirePasswordResetMessage />
        </>
      )}
      {tooManyFailedAttempts && (
        <>
          <Space v={1} />
          <TooManyFailedAttemptsMessage />
          {usingSso && ssoNotAvailable && <SsoNotAvailableMessage />}
          {oauthErrorKey && (
            <OAuthFailureMessage oauthErrorKey={oauthErrorKey} />
          )}
        </>
      )}
      {!authFactorMetaData && !hasRecoveryCode ? (
        <Box spaceAbove={[2, null, null, 3]} width="100%">
          <EmailAndPassword
            submitWithPassword={onSubmitWithPassword}
            submitWithSso={onSubmitWithSso}
            isTargetedPasswordReset={requireResetPassword}
            isSubmitting={isSubmitting}
            usingSso={usingSso}
            setSsoNotAvailable={setSsoNotAvailable}
          />

          {isOptimizelyReady && isSsoAvailable && usingSso ? (
            <Button
              onClick={() => setUsingSso(false)}
              variant={ButtonVariant.SecondaryOnLight}
              size={[ButtonSize.Lg, null, ButtonSize.Md, ButtonSize.Lg]}
            >
              <Trans>Sign in with a password</Trans>
            </Button>
          ) : null}
          {isOptimizelyReady && isSsoAvailable && !usingSso ? (
            <Button
              onClick={() => setUsingSso(true)}
              variant={ButtonVariant.SecondaryOnLight}
              size={[ButtonSize.Lg, null, ButtonSize.Md, ButtonSize.Lg]}
            >
              <Trans>Sign in with Single Sign On (SSO)</Trans>
            </Button>
          ) : null}
          <Visibility visible={["block", null, "none"]}>
            <Box spaceAbove={1} layout="flex" width="100%">
              <SignUpLink />
            </Box>
          </Visibility>
          <BankAccountDataBanner />
        </Box>
      ) : null}
      {!usingSso && authFactorMetaData && !hasRecoveryCode ? (
        <OneTimePassword
          spaceAbove={[2, null, null, 3]}
          authFactor={authFactorMetaData}
          submit={onSubmitWithPassword}
          isSubmitting={isSubmitting}
          triggerRecoveryCode={handleTriggerRecoveryCode}
        />
      ) : null}
      {!usingSso && hasRecoveryCode && (
        <>
          <Space v={[2, null, null, 3]} />
          <RecoveryCode
            submit={onSubmitWithPassword}
            triggerOneTimePassword={handleTriggerOneTimePassword}
          />
        </>
      )}
    </Box>
  );
};
